/**
 * Single-device session registry (client side).
 *
 * WHY THIS EXISTS
 * ---------------
 * `useSessionManager` validates `current_session_id` against `active_sessions`
 * every 20–30s and signs the user out when the server answers `valid:false`.
 *
 * The bug (P0, iOS native): `signIn()` writes a freshly minted `session_id` to
 * localStorage *before* the `manage-session` edge function has inserted the
 * row. On a cold iPhone launch over cellular the edge function can take longer
 * than the 5s `INITIAL_DELAY`, so the first validation ran against a row that
 * did not exist yet → `valid:false` → red toast → forced logout, seconds after
 * a perfectly good login. The same happened whenever the register call failed
 * (network blip): the id stayed in storage and every later validation failed.
 *
 * The registry makes registration state explicit: a session_id is only
 * "validatable" once the server has CONFIRMED the insert. Until then the
 * validator must skip. Nothing here talks to the network.
 */

export const SESSION_ID_KEY = "current_session_id";
export const SESSION_REGISTERED_KEY = "current_session_registered_at";

/** Monotonic generation, so a slow in-flight register cannot clobber a newer one. */
let registerGeneration = 0;

export function nextRegisterGeneration(): number {
  registerGeneration += 1;
  return registerGeneration;
}

export function isCurrentGeneration(gen: number): boolean {
  return gen === registerGeneration;
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Mint + persist a new session id, marked as NOT yet registered. */
export function mintSessionId(): string {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `sid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  safeSet(SESSION_ID_KEY, id);
  safeRemove(SESSION_REGISTERED_KEY);
  return id;
}

export function getSessionId(): string | null {
  return safeGet(SESSION_ID_KEY);
}

/** Called ONLY after the edge function confirms the insert. */
export function markSessionRegistered(sessionId: string): void {
  if (getSessionId() !== sessionId) return; // a newer id already won
  safeSet(SESSION_REGISTERED_KEY, `${sessionId}|${Date.now()}`);
}

/**
 * Register failed (network / edge error). Drop the unconfirmed id so the
 * validator skips instead of failing forever. The user stays signed in.
 */
export function clearUnconfirmedSession(sessionId: string): void {
  if (getSessionId() !== sessionId) return;
  safeRemove(SESSION_ID_KEY);
  safeRemove(SESSION_REGISTERED_KEY);
}

export function clearSessionRegistry(): void {
  safeRemove(SESSION_ID_KEY);
  safeRemove(SESSION_REGISTERED_KEY);
}

/** True only when THIS exact session_id was confirmed by the server. */
export function isSessionRegistered(sessionId: string | null): boolean {
  if (!sessionId) return false;
  const raw = safeGet(SESSION_REGISTERED_KEY);
  if (!raw) return false;
  return raw.split("|")[0] === sessionId;
}

/** Structured, PII-free logout audit line. */
export function logSessionDecision(
  reasonCode: string,
  data: Record<string, unknown> = {}
): void {
  try {
    // eslint-disable-next-line no-console
    console.log(`🔐 [SESSION][${reasonCode}]`, {
      ts: new Date().toISOString(),
      path: typeof window !== "undefined" ? window.location.pathname : null,
      platform: typeof navigator !== "undefined" ? navigator.platform : null,
      ...data,
    });
  } catch {
    /* ignore */
  }
}
